import { useMemo } from 'react';

import { mdiLinkVariant } from '@lumapps/lumx/icons';
import { useTranslate, GLOBAL } from '@lumapps/translations';
import { TypographyEditor } from '@lumapps/wrex-typography/types';
import { PLUGIN_SHORTCUTS } from '@lumapps/wrex/constants';
import { Editor, Range, ReactEditor, useSlate, useSlateStatic } from '@lumapps/wrex/slate';
import { focusAt } from '@lumapps/wrex/slate/utils/focusAt';
import { initSelection } from '@lumapps/wrex/slate/utils/initSelection';
import { ToolbarItem } from '@lumapps/wrex/types';

import { LINK } from '../../../constants';
import { WREX_LINK } from '../../../keys';
import { LinkEditor } from '../../../types';

export const useLinkButton = (otherProps?: any) => {
    const editor = useSlate() as ReactEditor & LinkEditor & TypographyEditor;
    const editorStatic = useSlateStatic() as ReactEditor & LinkEditor & TypographyEditor;
    const { translateAndReplace, translateKey } = useTranslate();

    const { selection } = editor;
    const currentLink = useMemo(() => {
        return selection ? Editor.parent(editor, Range.start(selection)) : undefined;
    }, [editor, selection]);
    const isSelected = editor.isLinkActive(currentLink);
    const isDisabled = !editor.isLinkAllowed();

    const linkButton = useMemo(() => {
        const onClick = () => {
            if (!editorStatic.selection) {
                initSelection(editorStatic, 'inline');
            }
            if (editorStatic.isLinkActive(currentLink)) {
                const currentLinkUrl = currentLink && currentLink[0] ? currentLink[0].href : '';
                editorStatic.wrapLink(editorStatic, currentLinkUrl as string, currentLink?.[1], true);
            } else {
                editorStatic.wrapLink(editorStatic, '');
            }
            focusAt(editorStatic, editorStatic.selection);
        };

        const linkButtonProps: ToolbarItem = {
            type: 'toggle-option',
            itemKey: 'link',
            icon: mdiLinkVariant,
            label: translateKey(GLOBAL.LINK),
            tooltipLabel: translateAndReplace(WREX_LINK.LINK_BUTTON_WITH_SHORTCUT, {
                shortcut: PLUGIN_SHORTCUTS[LINK].shortcut,
            }),
            variant: 'icon',
            onClick,
            'aria-disabled': isDisabled,
            isChecked: isSelected,
            otherProps,
        };
        return linkButtonProps;
    }, [translateAndReplace, translateKey, isDisabled, isSelected, otherProps, editorStatic, currentLink]);

    return linkButton;
};
